/**
 * navigationGuard.js
 *
 * Centralized document navigation and validation utility.
 *
 * ARCHITECTURE RULE:
 *   Direct navigate() calls are prohibited for document navigation.
 *   All document routing MUST use the helpers in this file.
 *   This ensures a valid document ID is always present before a route change.
 *
 * Error Codes:
 *   DOC_NOT_FOUND        — Document ID resolved but not in library
 *   INVALID_DOCUMENT_ID  — Backend returned missing/null/undefined ID
 *   INVALID_DOC_RESPONSE — Backend response missing required fields
 *   IMPORT_FAILED        — File import pipeline error
 *   NETWORK_TIMEOUT      — Request exceeded timeout threshold
 *   AUTOSAVE_FAILED      — Autosave write failed after retries
 *   EXPORT_FAILED        — PDF/DOCX export failed after retries
 *   RECOVERY_FAILED      — Route recovery could not find any valid document
 */

// ─── Error Codes ─────────────────────────────────────────────────────────────

export const ErrorCodes = {
  DOC_NOT_FOUND: 'DOC_NOT_FOUND',
  INVALID_DOCUMENT_ID: 'INVALID_DOCUMENT_ID',
  INVALID_DOC_RESPONSE: 'INVALID_DOC_RESPONSE',
  IMPORT_FAILED: 'IMPORT_FAILED',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  AUTOSAVE_FAILED: 'AUTOSAVE_FAILED',
  EXPORT_FAILED: 'EXPORT_FAILED',
  RECOVERY_FAILED: 'RECOVERY_FAILED',
};

export class AppError extends Error {
  constructor(code, message, cause = null) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.cause = cause;
  }
}

// ─── ID Resolver ─────────────────────────────────────────────────────────────

/**
 * Resolves a document ID from a backend response.
 * Supports multiple response shapes for backward compatibility.
 * Throws AppError(INVALID_DOCUMENT_ID) if no valid ID is found.
 */
export const resolveDocumentId = (result) => {
  const id =
    result?.metadata?.id ??
    result?.document_id ??
    result?.id ??
    null;

  if (!id || id === 'undefined' || id === 'null') {
    throw new AppError(
      ErrorCodes.INVALID_DOCUMENT_ID,
      `Backend did not return a valid document ID. Got: ${JSON.stringify(id)}. Response keys: ${Object.keys(result || {}).join(', ')}`
    );
  }

  return String(id);
};

// ─── Response Validator ───────────────────────────────────────────────────────

/**
 * Validates that a document response from the backend contains all required fields.
 * Throws AppError(INVALID_DOC_RESPONSE) with descriptive message if validation fails.
 */
export const validateDocumentResponse = (result, context = 'API call') => {
  if (!result || typeof result !== 'object') {
    throw new AppError(
      ErrorCodes.INVALID_DOC_RESPONSE,
      `[${context}] Response is null or non-object.`
    );
  }

  const id = result?.metadata?.id ?? result?.document_id ?? result?.id ?? null;
  const type = result?.metadata?.type ?? result?.type ?? null;

  if (!id || id === 'undefined') {
    throw new AppError(
      ErrorCodes.INVALID_DOC_RESPONSE,
      `[${context}] Response missing document ID. Keys: ${Object.keys(result).join(', ')}`
    );
  }

  if (!type) {
    throw new AppError(
      ErrorCodes.INVALID_DOC_RESPONSE,
      `[${context}] Response missing document type. ID was: ${id}`
    );
  }

  return true;
};

// ─── Navigation Helpers ───────────────────────────────────────────────────────

/**
 * Navigate to a resume workspace.
 * @param {Function} navigate - React Router navigate function.
 * @param {string} id - Document ID. Must be valid (not undefined/null).
 * @param {string} tab - Workspace tab to open ('chat' | 'editor').
 * @param {object} options - Optional React Router navigate options (e.g. { state, replace }).
 */
export const openResume = (navigate, id, tab = 'chat', options = {}) => {
  const safeId = String(id || '').trim();
  if (!safeId || safeId === 'undefined' || safeId === 'null') {
    console.error('[navigationGuard] openResume called with invalid ID:', id, '— falling back to master');
    navigate('/app/resume/master/chat', { replace: true });
    return;
  }
  navigate(`/app/resume/${safeId}/${tab}`, options);
};

/**
 * Navigate to a variant workspace.
 * Identical to openResume but semantically distinct for readability.
 */
export const openVariant = (navigate, id, tab = 'chat') => {
  return openResume(navigate, id, tab);
};

/**
 * Navigate to a cover letter workspace.
 * @param {Function} navigate - React Router navigate function.
 * @param {string} id - Cover letter document ID.
 * @param {object} options - Optional React Router navigate options (e.g. { state, replace }).
 */
export const openCoverLetter = (navigate, id, options = {}) => {
  const safeId = String(id || '').trim();
  if (!safeId || safeId === 'undefined' || safeId === 'null') {
    console.error('[navigationGuard] openCoverLetter called with invalid ID:', id, '— aborting navigation');
    return;
  }
  navigate(`/app/cover-letter/${safeId}`, options);
};

// ─── Timeout Wrapper ──────────────────────────────────────────────────────────

/**
 * Wraps a promise with a timeout.
 * Throws AppError(NETWORK_TIMEOUT) if the operation exceeds the limit.
 *
 * @param {Promise} promise - The async operation to wrap.
 * @param {number} timeoutMs - Timeout in milliseconds (default: 30000).
 * @param {string} context - Description for error messages.
 */
export const withTimeout = (promise, timeoutMs = 30000, context = 'operation') => {
  const timer = new Promise((_, reject) =>
    setTimeout(
      () => reject(new AppError(ErrorCodes.NETWORK_TIMEOUT, `[${context}] Request timed out after ${timeoutMs / 1000}s`)),
      timeoutMs
    )
  );
  return Promise.race([promise, timer]);
};

// ─── Retry Wrapper ────────────────────────────────────────────────────────────

/**
 * Executes an async function with automatic retry on failure.
 * Uses exponential backoff between attempts.
 *
 * @param {Function} fn - Async function to execute.
 * @param {number} maxRetries - Maximum retry attempts (default: 3).
 * @param {number} baseDelayMs - Initial delay between retries in ms (default: 1000).
 * @param {string} context - Description for logging.
 */
export const withRetry = async (fn, maxRetries = 3, baseDelayMs = 1000, context = 'operation') => {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      // Don't retry on user-facing validation errors
      if (err instanceof AppError && [
        ErrorCodes.INVALID_DOCUMENT_ID,
        ErrorCodes.INVALID_DOC_RESPONSE,
        ErrorCodes.DOC_NOT_FOUND,
      ].includes(err.code)) {
        throw err;
      }
      if (attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        console.warn(`[navigationGuard] ${context} failed on attempt ${attempt}/${maxRetries}. Retrying in ${delay}ms...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  console.error(`[navigationGuard] ${context} failed after ${maxRetries} attempts.`, lastError);
  throw lastError;
};
